import get from 'lodash/get'
import { APPLICATION_REQUIREMENT_REF_PREFIX } from './requirementRef.util'

// "application:<id>" is this domain's own sentinel prefix — distinct from group requirements'
// "group:<id>" and named location requirements' "location:<id>" — so a raw policyBody value is
// unambiguous about which kind of requirement it diverts to, without needing to know which field
// it came from.
export const REQUIREMENT_REF_PREFIX = APPLICATION_REQUIREMENT_REF_PREFIX

export const isRequirementRef = (value) => typeof value === 'string' && value.startsWith(REQUIREMENT_REF_PREFIX)

export const getRequirementId = (value) => (isRequirementRef(value) ? value.slice(REQUIREMENT_REF_PREFIX.length) : null)

export const toRequirementRef = (requirementId) => `${REQUIREMENT_REF_PREFIX}${requirementId}`

// Must match backend applicationRequirementUtils.toChosenCandidateKey. Group and application
// requirement ids are both display names, so application multi-match picks are prefixed before
// they share a chosenCandidates payload with group picks.
export const APPLICATION_CHOSEN_CANDIDATE_PREFIX = 'application-choice:'

export const toApplicationChosenCandidateEntries = (chosenCandidates = {}) =>
  Object.fromEntries(
    Object.entries(chosenCandidates).map(([requirementId, graphApplicationId]) => [
      `${APPLICATION_CHOSEN_CANDIDATE_PREFIX}${requirementId}`,
      graphApplicationId,
    ]),
  )

// Mirrors backend/src/lambdas/service/hub/microsoft/applicationRequirements/
// resolveApplicationRequirements.js's RESOLUTION_STATUS. No CREATABLE status exists here —
// Augmentt never creates applications, so a requirement that finds no match is unresolvable.
export const APPLICATION_REQUIREMENT_STATUS = {
  MATCHED: 'matched',
  MULTI_MATCH: 'multiMatch',
  UNRESOLVABLE: 'unresolvable',
}

// Template meta_data key holding { [appId]: name } for the applications a template keeps as-is in
// policyBody, captured from the copied tenant when the template is saved. A reopened template has
// no tenant to look the names up in. Mirrors the backend's applicationRequirementUtils.js.
export const APPLICATION_NAMES_META_KEY = 'applicationNames'

const APPLICATION_FIELD_PATHS = [
  'conditions.applications.includeApplications',
  'conditions.applications.excludeApplications',
]

// The meta_data entry naming every application id in the policy that nameByAppId knows, or
// nothing when none of them is named — spread straight into a template's meta_data.
export const getApplicationNamesMeta = (policyBody, nameByAppId = {}) => {
  const names = {}

  APPLICATION_FIELD_PATHS.forEach((path) => {
    const values = get(policyBody, path)
    ;(Array.isArray(values) ? values : []).forEach((appId) => {
      if (nameByAppId[appId]) {
        names[appId] = nameByAppId[appId]
      }
    })
  })

  return Object.keys(names).length > 0 ? { [APPLICATION_NAMES_META_KEY]: names } : {}
}
